import { add, format, formatISO9075, subDays } from "date-fns"
import moment, { Moment } from "moment/moment"
import { TimeConvention } from "../zstore/LocalStorageStore"
import { keysForStringEnum } from "./EnumUtils"

interface TimeFormatOptions {
    format?: TimeConvention
    includeSeconds?: boolean
}

/**
 * @deprecated Please use TimeUtilsDateFns from now on
 */
export class TimeUtils {
    static invalidDate = "Invalid date"

    static dateFormat = "YYYY-MM-DD"

    static hourMinuteSecond_24 = "HH:mm:ss"
    static hourMinute_24 = "HH:mm"
    static hourMinuteSecond_12 = "hh:mm:ss A"
    static hourMinute_12 = "hh:mm A"

    static formatDate = (date?: moment.MomentInput) => moment(date).format(this.dateFormat)

    static formatTime = (time?: string, options?: TimeFormatOptions) => {
        const formatTimeString = this.getTimeFormatString(options)
        return time ? format(new Date(time), formatTimeString) : ""
    }

    static parseDate = (d?: string | null) => (d != null ? moment(d, this.dateFormat) : undefined)
    static parseDateTimeHMS = (d?: string, t?: string) => {
        if (TimeUtils.validDate(d) && TimeUtils.validDate(t)) {
            return moment(`${d} ${t}`, `${this.dateFormat} ${this.hourMinuteSecond_24}`)
        }
    }

    static validDate = (date?: string | Moment): boolean => !!(date && date !== TimeUtils.invalidDate)

    static getTimeFormatString = (options?: TimeFormatOptions): string => {
        const { format = TimeConvention.TWENTY_FOUR, includeSeconds = false } = options ?? {}

        if (format === TimeConvention.TWELVE) {
            return includeSeconds ? this.hourMinuteSecond_12 : this.hourMinute_12
        }
        if (format === TimeConvention.TWENTY_FOUR) {
            return includeSeconds ? this.hourMinuteSecond_24 : this.hourMinute_24
        }

        throw new Error(
            `The param ${format} in getFormat() is not a valid time convention: ${keysForStringEnum(TimeConvention)}`,
        )
    }
}

// TODO - remove above class that uses moment and only use this class and Date fns
export class TimeUtilsDateFns {
    // Date FNS formats
    static dayFormat = "yyyy-MM-dd"
    private static timeFormat_24 = "HH:mm"
    private static timeFormat_24_WithSeconds = "HH:mm:ss"
    private static timeFormat_12 = "hh:mm a..aa"
    private static timeFormat_12_WithSeconds = "hh:mm:ss a..aa"

    // Date FNS functions
    static formatISOStringLocal = (dateTime: Date) => {
        // https://date-fns.org/v4.1.0/docs/formatISO9075
        // Date-fns formatISO() automatically adds a timezone at the end, whereas formatISO9075() does not
        return formatISO9075(dateTime).replace(" ", "T")
    }

    static formatDate = (date: Date) => format(date, this.dayFormat)

    static formatTime = (date?: Date, options?: TimeFormatOptions) => {
        const formatTimeString = this.getTimeFormat(options)
        return date && format(date, formatTimeString)
    }

    static getFormattedNumberOfDaysAgo = (num: number) => {
        const numDaysAgo = subDays(new Date(), num)
        return TimeUtilsDateFns.formatDate(numDaysAgo)
    }

    static getDateFromFormattedDay = (formattedDay: string) => {
        const dateArray = formattedDay.split("-")
        const year = +dateArray[0]
        const month = Number.parseInt(dateArray[1], 10) - 1
        const day = +dateArray[2]

        return new Date(year, month, day)
    }

    static addTimeToDate = (date: Date, timeString: string) => {
        const timeArray = timeString.split(":")
        const hours = getNumberOrUndefined(timeArray[0])
        const minutes = getNumberOrUndefined(timeArray[1])
        const seconds = getNumberOrUndefined(timeArray[2])
        return add(date, { hours, minutes, seconds })
    }

    static getTimeFormat = (options?: TimeFormatOptions): string => {
        const { format = TimeConvention.TWENTY_FOUR, includeSeconds = false } = options ?? {}

        if (format === TimeConvention.TWELVE) {
            return includeSeconds ? this.timeFormat_12_WithSeconds : this.timeFormat_12
        }
        if (format === TimeConvention.TWENTY_FOUR) {
            return includeSeconds ? this.timeFormat_24_WithSeconds : this.timeFormat_24
        }

        throw new Error(
            `The param ${format} in getFormat() is not a valid time convention: ${keysForStringEnum(TimeConvention)}`,
        )
    }
}

const getNumberOrUndefined = (val?: string) => (val ? +val : undefined)
